import Image from "@Components/Image";
import OfferChevronDown from "@Icons/OfferChevronDown";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import getImagePath from "@Utils/getImagePath";
import { lazy, memo, Suspense, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getFeaturedImage,
  getFeaturedImageAtFirst,
  getFormattedGalleryAssets,
  getFormattedTags,
  getMinimumAvailableFromValue,
  getPricingPriceFormatted,
  isAvailable,
} from "../../utils";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
import styles from "./RoomTypeSection.module.scss";
import TenanciesSection from "./components/TenanciesSection";
import ViewMoreDetailsModal from "./components/ViewMoreDetailsModal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useRoomTypeContext from "../../context/useRoomTypeContext";
import ThreeDIcon from "@Icons/ThreeDIcon";
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import wrapperHOC from "@Utils/wrapperHOC";
import TagV2 from "./components/Tag/TagV2";
import PhotosIcon from "@Icons/PhotosIcon";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import ThreeDIcon2 from "@Icons/ThreeDIcon2";
import PlayIcon from "@Icons/PlayIcon";
import { getVideoThumb } from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import { getTranslatedDurationValue } from "./components/TenancyCard/utils";
import { AppState } from "@Store/index";
import FutureIntakeStrip from "./components/FutureIntakeStrip";
import Portal from "@Components/Portal";
import Modal from "@Components/Modal";
import FullPageLoader from "@Components/FullPageLoader";
import {
  getRoomMediaModel,
  RoomMediaTabId,
} from "@Pages/PropertyPage/components/RoomMediaGallery/roomMedia";
import useOpenVideoModal from "@Pages/PropertyPage/hooks/useOpenVideoModal";
import { getModalsFromState, unwrapState } from "@Components/Modal/useModal";
import useRoomDetailsVariant from "@Pages/PropertyPage/hooks/useRoomDetailsVariant";
import { ROOM_DETAILS_SECTION, ROOM_DETAILS_VARIANT } from "helpers/roomDetailsTracking";

// Lazy, like the property gallery: keeps LayoutV2 and the player out of the PDP bundle.
const RoomMediaModal = lazy(
  () => import("@Pages/PropertyPage/components/RoomMediaGallery/RoomMediaModal"),
);

// Written into the gallery's history entry so Back can reopen the right room and tab.
const ROOM_GALLERY_STATE_KEY = "roomMediaRoomId";
const ROOM_GALLERY_TAB_KEY = "roomMediaTab";

const readEntryState = () => {
  const raw = typeof window !== "undefined" ? window.history?.state : null;
  const inner = unwrapState(raw);
  return { raw, inner, modals: getModalsFromState(inner) };
};

type RoomTypeCardProps = {
  id?: string;
  data?: any;
  isDesktop?: boolean;
  activeTab?: string;
  nextRoomId?: string;
};

type AmenityTag = {
  text: string;
  icon: string;
  key: string;
};

const RoomTypeCardV2 = ({
  id,
  data,
  isDesktop = false,
  activeTab = "",
  nextRoomId = "",
}: RoomTypeCardProps) => {
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { propertyData, roomTypes } = useRoomTypeContext();
  const openVideoModal = useOpenVideoModal();
  const [showMoreDetailsModal, setShowMoreDetailsModal] = useState(false);
  const [shouldModalScroll, setShouldModalScroll] = useState(false);
  const { showVideoModal, roomMediaModal, moveInMonth } = useSelector(
    ({ propertyPageData }: AppState) => ({
      showVideoModal: propertyPageData?.video_modal?.show,
      roomMediaModal: propertyPageData?.room_media_modal,
      moveInMonth: propertyPageData?.filters?.moveInMonth,
    }),
    shallowEqual,
  );

  const resolveRoomDetailsVariant = useRoomDetailsVariant();

  // `room_details_viewed` / `_closed` are sent by the modal and the side panel themselves (P1D-5512),
  // once their tenancies are known, so both arms report the same properties.
  const openModal = (shouldScroll = false) => {
    // The arm is decided here, at click time: evaluating the GrowthBook flag is what buckets the
    // user and sends `experiment_viewed`. `null` = not in the test (mobile, <1200px, ...).
    const variant = resolveRoomDetailsVariant();
    if (variant === ROOM_DETAILS_VARIANT.SIDE_PANEL) {
      propertyPageDispatcher.showRoomDetailsPanel({
        show: true,
        roomId: data.id,
        openSource: "view_more_details",
      });
      return;
    }
    if (shouldScroll && !isDesktop) setShouldModalScroll(true);
    setShowMoreDetailsModal(true);
  };
  const closeModal = () => {
    setShowMoreDetailsModal(false);
    setShouldModalScroll(false);
  };
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const { isWhitelabel } = useWhitelabel();
  const isMiniProgram = useIsMiniProgram();
  const isRootInventoryPricePerPersonEnabled = propertyData?.pricing?.price_per_person || false;
  const isSharedRoom = data?.meta?.types?.includes("shared_room");
  const price = getPricingPriceFormatted(
    { ...data.pricing, price_per_person: isSharedRoom && isRootInventoryPricePerPersonEnabled },
    { price_per_person: true },
  );
  const available = isAvailable(data);
  const minAvailableFrom = getMinimumAvailableFromValue(
    available,
    data?.meta,
    data?.active_children_with_filters,
    moveInMonth,
  );
  const isFlexible =
    data?.active_children_with_filters?.length === 0 ||
    (data?.active_children_with_filters || []).some(
      (item: any) => item.available && !item?.meta?.available_from,
    );
  const featuredImage =
    data?.images.length > 0
      ? getFeaturedImage(data?.images)
      : getFeaturedImage(propertyData?.images);
  const tags: AmenityTag[] = getFormattedTags(data, t) || [];
  const excludeTags = [t("Floor")];
  const tagsAfterRemoval = tags.filter((tag) => !excludeTags.includes(tag?.key));
  const featuredImageAsset = [
    {
      ...featuredImage,
      configName: data?.name,
      category: "image",
    },
  ];
  const roomAssets = getFormattedGalleryAssets(data, !isWhitelabel);
  const translatedPrice = getTranslatedDurationValue(price, t, "/");

  const assetsLength = Number(roomAssets?.length || featuredImageAsset?.length);
  const images = data?.images?.length ? getFeaturedImageAtFirst(data?.images) : [featuredImage];
  const shouldShow3D = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_TYPE_3D);

  // Default-on kill switch (useFeatureIsOn is false before GrowthBook loads). GrowthBook
  // never loads on China, so the flag cannot turn it off there. Whitelabel is excluded.
  const isRoomMediaGalleryOn =
    useFeatureValue(GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_TYPE_MEDIA_GALLERY, true) && !isWhitelabel;

  // `roomTypes.data` can lack videos (children_short has them) and images (use the
  // property's featured image).
  const childData = propertyData?.children_short?.find((child: any) => child.id === data?.id);
  const roomDataForGallery = {
    ...data,
    images: data?.images?.length
      ? data.images
      : [getFeaturedImage(propertyData?.images)].filter(Boolean),
    videos: data?.videos?.length ? data.videos : childData?.videos || [],
  };
  // No 3D in the mini-program, matching GalleryModalV2 (not covered by shouldShow3D).
  const roomMediaModel = getRoomMediaModel(roomDataForGallery, {
    show3D: shouldShow3D && !isMiniProgram,
  });
  // In Redux so the video modal's "View Rooms" can close it; `roomId` keeps one card open.
  // The side panel hosts the gallery it opens itself (this card can be unmounted), so skip those.
  const showRoomMediaModal =
    Boolean(roomMediaModal?.show) &&
    roomMediaModal?.roomId === data?.id &&
    roomMediaModal?.source !== "side_panel";
  const closeRoomMediaModal = () => propertyPageDispatcher.closeRoomMediaModal();

  // Close on navigation away. Keyed on pathname + hash, not `location.key`: the
  // gallery's own history push keeps the path, and `key` would close it on open.
  const { pathname, hash } = useLocation();
  const routeKey = `${pathname}${hash}`;
  const openedAtRouteRef = useRef("");
  useEffect(() => {
    if (!showRoomMediaModal) {
      openedAtRouteRef.current = "";
      return;
    }
    if (!openedAtRouteRef.current) {
      openedAtRouteRef.current = routeKey;
      return;
    }
    // Dropping a hash (RoomTypeContextProvider's room-hash cleanup) is not navigating away.
    if (!hash && openedAtRouteRef.current.startsWith(`${pathname}#`)) {
      openedAtRouteRef.current = routeKey;
      return;
    }
    if (openedAtRouteRef.current !== routeKey) closeRoomMediaModal();
  }, [routeKey, showRoomMediaModal]);

  // Stamp room + tab into the entry useModal just pushed (child effects run first).
  useEffect(() => {
    if (!showRoomMediaModal || typeof window === "undefined") return;
    const { raw, inner, modals } = readEntryState();
    if (!modals.includes("roomMediaGallery")) return;
    if (inner[ROOM_GALLERY_STATE_KEY] === data?.id) return;
    window.history.replaceState(
      {
        ...raw,
        state: {
          ...inner,
          [ROOM_GALLERY_STATE_KEY]: data?.id,
          [ROOM_GALLERY_TAB_KEY]: roomMediaModal?.initialTab || "photos",
        },
      },
      "",
      window.location.href,
    );
  }, [showRoomMediaModal]);

  // Reopen on Back to the gallery's entry. Location-keyed, not popstate: the cards
  // remount after VideoPage and would miss the event.
  useEffect(() => {
    if (roomMediaModal?.show) return;
    const { inner, modals } = readEntryState();
    if (!modals.includes("roomMediaGallery")) return;
    if (inner[ROOM_GALLERY_STATE_KEY] !== data?.id) return;
    propertyPageDispatcher.showRoomMediaModal({
      show: true,
      roomId: data?.id,
      initialTab: inner[ROOM_GALLERY_TAB_KEY] || "photos",
    });
  }, [pathname, hash, data?.id, roomMediaModal?.show]);
  const virtualView = data?.virtual_views?.map((item: any) => ({
    ...item,
    category: "3D",
    caption: "3D",
    configName: data?.name,
  }));
  const videos = (data?.videos || []).map((item: any) => ({
    ...item,
    category: "videos",
    configName: data?.name,
  }));

  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;
  const floorPlanImages = data?.images?.filter((image: any) => image.type === "floor_plan") || [];

  // const depositLabel = getDepositTextLabel(paymentType);

  // const deposit =
  //   paymentType === "deposit"
  //     ? getPricingDepositFormatted(data.pricing)
  //     : formatTokenPlatformFees(paymentType, propertyData.pricing);

  // Ordered gallery assets for the carousel: images first, then videos, then 3D views.
  const galleryAssets = [
    ...images.map((item: any) => ({ ...item, category: item?.category || "image" })),
    ...videos,
    ...(shouldShow3D ? virtualView : []),
  ];

  // `assets` must be the array `index` came from: the strip's galleryAssets, or a gallery section.
  const openRoomCarousel = (
    index: any,
    label = "image",
    assets: any[] = galleryAssets,
    // Opened over the room gallery, which already holds the body lock.
    fromGallery = false,
  ) => {
    gtagEventEmitter({
      action: "room-type-image-clicked",
      category: "room-type",
      label,
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: assets,
      category: "room-type",
      currentItemIndex: index,
      showViewRoomButton: false,
      config_id: data.id,
      fromGallery,
    });
  };

  /** Which gallery tab a strip click should land on. */
  const getInitialTab = (label: string) => {
    if (label === "video") return "video_tour" as const;
    if (label === "3D") return "3d_tour" as const;
    return "photos" as const;
  };

  const handleFeaturedImageClick = (index: any, label = "image") => {
    if (isRoomMediaGalleryOn && roomMediaModel.totalCount > 0) {
      gtagEventEmitter({
        action: "room_media_modal_opened",
        category: "room-type",
        label: data?.name,
      });
      propertyPageDispatcher.showRoomMediaModal({
        show: true,
        roomId: data?.id,
        initialTab: getInitialTab(label),
      });
      return;
    }
    openRoomCarousel(index, label);
  };

  const handleBadgeClick = (index: number, label: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    handleFeaturedImageClick(index, label);
  };

  const handleBadgeKeyDown = (index: number, label: string) => (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.stopPropagation();
    event.preventDefault();
    handleFeaturedImageClick(index, label);
  };

  const handleFloorPlanClick = () => {
    gtagEventEmitter({
      action: "room-type-floor-plan-clicked",
      category: "room-type",
      label: "floor_plan",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: floorPlanImages,
      category: "floor-plan",
      currentItemIndex: 0,
      showViewRoomButton: false,
      config_id: data.id,
    });
  };

  const amenityTags: any = [
    { name: t("propertypage.roomtypesectiondynamic.roomtypecard.roomdetailsheading"), values: [] },
    ...(Array.isArray(data?.features) ? data.features : []),
  ];
  tags?.forEach((tag: any) => {
    amenityTags[0].values.push({
      ...tag,
      name: tag?.text,
    });
  });

  const avbFromText = t("propertypage.roomtypesectiondynamic.roomtypecard.availablefrom");
  const is3DPresent = virtualView?.length > 0;
  const isVideoPresent = videos?.length > 0;
  // Start index of each category within galleryAssets (images -> videos -> 3D).
  const videosStartIndex = images.length;
  const virtualViewStartIndex = images.length + videos.length;

  return (
    <div
      className={formatClassNames(styles.parentContainer, isDesktop && styles.desktop)}
      id={id}
      data-testid="room-types-card"
    >
      <div className={styles.infoSection}>
        <div className={formatClassNames(styles.herov2, isDesktop && styles.desktop)}>
          {isDesktop ? (
            <div
              className={styles.imageContainer}
              onClick={() => handleFeaturedImageClick(0)}
              role="button"
              tabIndex={0}
              onKeyDown={() => handleFeaturedImageClick(0)}
            >
              <Image
                dataTestId="propety-room-type-image"
                src={getImagePath(featuredImage?.path, "small")}
                className={styles.image}
                height={140}
                width={140}
                alt="Room type image"
              />
              <div className={styles.smallIconTagsContainer}>
                <div className={styles.imageCount}>
                  <PhotosIcon width="12px" height="12px" fill="white" />
                  {Number(images.length)}
                </div>
                {/* Icon only: the photo badge is the card's only count. */}
                {/* Own handlers, so the badge opens its own tab rather than the image's Photos. */}
                {isVideoPresent && (
                  <div
                    className={formatClassNames(styles.imageCount)}
                    role="button"
                    tabIndex={0}
                    aria-label={t("propertypage.components.roommediagallery.videotour")}
                    data-testid="room-type-video-badge"
                    onClick={handleBadgeClick(videosStartIndex, "video")}
                    onKeyDown={handleBadgeKeyDown(videosStartIndex, "video")}
                  >
                    <PlayIcon width="12" height="12" fill="white" />
                  </div>
                )}
                {is3DPresent && (
                  <div
                    className={formatClassNames(styles.imageCount)}
                    role="button"
                    tabIndex={0}
                    aria-label={t("propertypage.components.roommediagallery.threedtour")}
                    data-testid="room-type-3d-badge"
                    onClick={handleBadgeClick(virtualViewStartIndex, "3D")}
                    onKeyDown={handleBadgeKeyDown(virtualViewStartIndex, "3D")}
                  >
                    <ThreeDIcon2 width={14} height={14} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!isDesktop && (
                <h3
                  data-testid="mobile-propety-room-type-name"
                  className={formatClassNames(styles.titleV2, isWhitelabel ? "wl-title-font" : "")}
                >
                  {data.name || ""}
                </h3>
              )}
              <div className={styles.horizontalScroll}>
                {images.map((img: any, idx: any) => (
                  <div key={`imgs${idx}`} className={styles.imageContainerV2}>
                    <Image
                      dataTestId="mobile-property-room-type-images"
                      src={getImagePath(img?.path, "room-type-card")}
                      className={styles.imageMobile}
                      height={122}
                      width={211}
                      alt="Room type image"
                      addShimmer
                      onClick={() => handleFeaturedImageClick(idx, "image")}
                    />
                    {idx === 0 && (
                      <div className={styles.imageCount}>
                        <PhotosIcon width="12px" height="12px" fill="white" />
                        {Number(roomAssets.length || featuredImageAsset.length)}
                      </div>
                    )}
                  </div>
                ))}
                {videos.map((video: any, idx: any) => (
                  <div
                    key={`video${idx}`}
                    className={styles.imageMobileContainer}
                    onClick={() => handleFeaturedImageClick(videosStartIndex + idx, "video")}
                    role="none"
                  >
                    <Image
                      dataTestId="mobile-property-room-type-videos"
                      src={getVideoThumb(video)}
                      className={styles.imageMobile}
                      height={122}
                      width={211}
                      alt={video?.type || "Room type video"}
                      addShimmer
                    />
                    <div className={styles.overlay}>
                      <PlayIcon width="28" height="28" fill="white" />
                    </div>
                  </div>
                ))}
                {shouldShow3D &&
                  virtualView?.map((item: any, index: any) => (
                    <div
                      key={`3d${index}`}
                      className={styles.imageMobileContainer}
                      onClick={() => handleFeaturedImageClick(virtualViewStartIndex + index, "3D")}
                      role="none"
                    >
                      <Image
                        dataTestId="mobile-property-room-type-3d"
                        src={getImagePath(images[index]?.path, "room-type-card")}
                        className={styles.imageMobile}
                        height={122}
                        width={211}
                        alt={item?.type}
                        addShimmer
                      />
                      <div className={styles.overlay}>
                        <ThreeDIcon />
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
          <div className={styles.info}>
            {isDesktop && (
              <h3
                data-testid="propety-room-type-name"
                className={formatClassNames(
                  isWhitelabel ? "wl-title-font" : "",
                  styles.titleV2,
                  isDesktop && styles.desktop,
                )}
              >
                {data?.name || ""}
              </h3>
            )}
            <div
              className={formatClassNames(
                styles.descriptionv2,
                isDesktop && styles.desktop,
                !available && styles.row,
              )}
            >
              {minAvailableFrom && isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={formatClassNames(
                    styles.availabilityContainer,
                    config.IS_CHINA_APP && styles.availabilityContainerChina,
                  )}
                >
                  {/* change is needed here */}
                  {avbFromText}:{" "}
                  <span>
                    {config.IS_CHINA_APP
                      ? renderChinaNamedDate(minAvailableFrom, minAvailableFrom)
                      : minAvailableFrom}
                  </span>
                </span>
              )}
              {isFlexible && available && isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={formatClassNames(
                    styles.availabilityContainer,
                    config.IS_CHINA_APP && styles.availabilityContainerChina,
                  )}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {minAvailableFrom && !isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={formatClassNames(
                    styles.availabilityContainerMobile,
                    config.IS_CHINA_APP && styles.availabilityContainerMobileChina,
                  )}
                >
                  {avbFromText} :{" "}
                  <span>
                    {config.IS_CHINA_APP
                      ? renderChinaNamedDate(minAvailableFrom, minAvailableFrom)
                      : minAvailableFrom}
                  </span>
                </span>
              )}
              {isFlexible && available && !isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={formatClassNames(
                    styles.availabilityContainerMobile,
                    config.IS_CHINA_APP && styles.availabilityContainerMobileChina,
                  )}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {!available && (
                <span
                  data-testid="property-room-type-tenancy-soldout"
                  className={formatClassNames(styles.soldOutBadge, isDesktop && styles.desktop)}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.soldout")}
                </span>
              )}
              {(isDesktop ||
                (!available && !(isFloorPlansSectionEnabled && floorPlanImages.length > 0))) && (
                <span className={styles.dot}>·</span>
              )}
              {!isDesktop &&
                available &&
                !(isFloorPlansSectionEnabled && floorPlanImages.length > 0) && (
                  <>
                    <span className={styles.dot}>·</span>
                    <span
                      data-testid="property-room-type-price-range"
                      className={styles.priceContainer}
                    >
                      {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                    </span>
                  </>
                )}
              {!isDesktop &&
                !available &&
                !(isFloorPlansSectionEnabled && floorPlanImages.length > 0) && (
                  <span
                    data-testid="property-room-type-price-range"
                    className={styles.priceContainer}
                  >
                    {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                  </span>
                )}
              {isDesktop && (
                <>
                  <span
                    data-testid="property-room-type-price-range"
                    className={styles.priceContainer}
                  >
                    {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                  </span>
                  {isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
                    <>
                      <span className={styles.dot}>·</span>
                      <span
                        className={styles.viewFloorPlanInline}
                        onClick={handleFloorPlanClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                        }}
                        data-testid="room-type-view-floor-plan"
                      >
                        <FloorPlanIcon size="16" />
                        <span>
                          {t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}
                        </span>
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            {!isDesktop && isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
              <div className={styles.priceFloorPlanRow}>
                <span
                  data-testid="property-room-type-price-range"
                  className={styles.priceContainer}
                >
                  {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                </span>
                <span
                  className={styles.viewFloorPlanInline}
                  onClick={handleFloorPlanClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                  }}
                  data-testid="room-type-view-floor-plan"
                >
                  <FloorPlanIcon size="16" />
                  <span>{t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}</span>
                </span>
              </div>
            )}
            {isDesktop && (
              <div
                className={formatClassNames(
                  styles.amenitiesContainerV2,
                  isDesktop && styles.desktop,
                )}
              >
                {tagsAfterRemoval.slice(0, 6).map((item) => (
                  <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
                ))}
              </div>
            )}
            {isDesktop && (
              <button
                type="button"
                onClick={() => openModal()}
                className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}
              >
                <span
                  data-testid="property-room-type-view-more-details"
                  className={styles.viewMoreText}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoredetails")}
                </span>
                <span className={styles.viewMoreIcon}>
                  <OfferChevronDown height="16" width="16" />
                </span>
              </button>
            )}
            {showMoreDetailsModal && (
              <ViewMoreDetailsModal
                heading={data.name}
                // tenancies={data?.active_children}
                roomTypeData={data}
                onClose={closeModal}
                isSoldOut={!available}
                assetsLength={assetsLength}
                images={images}
                availableFrom={minAvailableFrom}
                isFlexible={isFlexible}
                features={amenityTags}
                featuredImage={featuredImage}
                description={data?.description[0]}
                virtualView={virtualView}
                shouldModalScroll={shouldModalScroll}
                activeTab={activeTab}
                trackViewEvents
              />
            )}
          </div>
        </div>
        {!isDesktop && (
          <div className={styles.amenitiesWithButton}>
            <div className={styles.amenities}>
              {tags.slice(0, 6).map((item) => (
                <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
              ))}
              <button
                type="button"
                onClick={() => openModal()}
                className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}
              >
                <span
                  data-testid="mobile-property-room-type-view-more-details"
                  className={styles.viewMoreText}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoredetails")}
                </span>
                <span className={styles.viewMoreIcon}>
                  <OfferChevronDown height="12" width="12" />
                </span>
              </button>
            </div>
          </div>
        )}
        <FutureIntakeStrip
          roomTypeData={data}
          featuredImage={featuredImage}
          isDesktop={isDesktop}
          sectionName={ROOM_DETAILS_SECTION.ROOM_CARD}
        />
        <TenanciesSection
          tenancies={data?.active_children_with_filters}
          roomTypeData={data}
          // soldout tenancies fix by sainath arjun
          isSoldOut={!available}
          featuredImage={featuredImage}
          activeTab={activeTab}
          isDesktop={isDesktop}
          setShowMoreDetailsModal={() => openModal(true)}
          nextRoomId={nextRoomId}
          sectionName={ROOM_DETAILS_SECTION.ROOM_CARD}
        />
      </div>

      {/* Room Type Gallery — the property gallery's layout, scoped to this room. */}
      {showRoomMediaModal && (
        <Portal rootId="forPortal">
          <Modal
            /* Distinct from "galleryModal": `type` keys useModal's history stack. */
            type="roomMediaGallery"
            hideModal={closeRoomMediaModal}
            size="fullScreen"
            animation="bottomToTop"
            isHistoryPush
            /* Escape must not orphan a video modal open on top. */
            disableEscapeKey={showVideoModal}
          >
            <Suspense fallback={<FullPageLoader />}>
              <RoomMediaModal
                model={roomMediaModel}
                roomName={data?.name || ""}
                room={data}
                isDesktop={isDesktop}
                initialTab={(roomMediaModal?.initialTab as RoomMediaTabId) || "photos"}
                // The gallery stays mounted under the carousel, as in GalleryV2: closing
                // it in the same tick races useModal's goBack against the carousel's push.
                onItemClick={(indexInSection: number, item: any, sectionItems: any[]) => {
                  // Videos open the video modal; no resolvable id falls through to the carousel.
                  if (
                    item?.category === "videos" &&
                    openVideoModal(item, {
                      roomTypes: roomTypes?.data || [],
                      eventLabel: data?.name,
                      roomId: data?.id,
                    })
                  ) {
                    return;
                  }
                  openRoomCarousel(indexInSection, "modal", sectionItems, true);
                }}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
    </div>
  );
};

export default wrapperHOC(memo(RoomTypeCardV2), {
  componentName: "RoomTypeCardV2-RoomTypeSection-PropertyPage",
  showForChina: true,
});
